import { Component, OnInit, inject, Input, Output, EventEmitter, effect, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { PaymentService } from '../../services/payment.service';
import { CatalogService } from '@services/data/catalog.service';
import { PeriodService } from '../../../billing-management/services/period.service';
import { AuthService } from '@services/core/auth.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-payment-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
  templateUrl: './payment-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./payment-form.component.scss']
})
export class PaymentFormComponent implements OnInit {
  @Input() entityCode: number | null = null;
  @Input() mode: 'PAYMENT' | 'ADJUSTMENT' = 'PAYMENT';
  @Output() onSave = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();

  paymentForm: FormGroup;
  entities: any[] = [];
  periods: any[] = [];
  selectedFile: File | null = null;
  isSaving = false;

  movementTypes = [
    { code: 'PAGO', name: 'Pago Regular de Entidad (Abono que reduce deuda)' },
    { code: 'AJUSTE_ABONO', name: 'Ajuste Contable - Abono a Favor Entidad (Reduce Deuda)' },
    { code: 'AJUSTE_CARGO', name: 'Ajuste Contable - Cargo a Favor FPV (Incrementa Deuda)' }
  ];

  get availableMovementTypes() {
    if (this.mode === 'ADJUSTMENT') {
      return this.movementTypes.filter(m => m.code !== 'PAGO');
    }
    return this.movementTypes;
  }

  private fb = inject(FormBuilder);
  private paymentService = inject(PaymentService);
  private catalogService = inject(CatalogService);
  private periodService = inject(PeriodService);
  private authService = inject(AuthService);

  constructor() {
    this.paymentForm = this.fb.group({
      movementType: ['PAGO', Validators.required],
      entityCode: [null, Validators.required],
      periodId: [null],
      amount: [null, [Validators.required, Validators.min(1)]],
      paymentDate: [new Date().toISOString().split('T')[0], Validators.required],
      details: ['']
    });

    effect(() => {
      this.loadEntities();
    });
    effect(() => {
      this.periods = this.periodService.billingPeriods();
    });
  }

  ngOnInit(): void {
    this.loadEntities();
    if (this.mode === 'ADJUSTMENT') {
      this.paymentForm.patchValue({ movementType: 'AJUSTE_ABONO' });
    } else {
      this.paymentForm.patchValue({ movementType: 'PAGO' });
    }
    if (this.entityCode) {
      this.paymentForm.patchValue({ entityCode: this.entityCode });
    }
  }

  /** Solo entidades del rol activo (user_role_entity). */
  loadEntities() {
    const assigned = (this.authService.sharingEntities() as any[]) || [];
    const catalog = this.catalogService.entities() || [];
    const byCode = new Map(catalog.map((e: any) => [Number(e.code), e]));
    this.entities = assigned
      .map((e: any) => {
        const code = Number(e.code ?? e.entityCode);
        if (!Number.isFinite(code)) return null;
        const cat = byCode.get(code);
        const name = cat?.name ?? e.name ?? '';
        return { ...(cat || {}), ...e, code, name: `${code} - ${name}` };
      })
      .filter(Boolean) as any[];

    const selected = this.paymentForm?.get('entityCode')?.value;
    if (selected != null && !this.entities.some((e) => Number(e.code) === Number(selected))) {
      this.paymentForm.patchValue({ entityCode: this.entities.length === 1 ? this.entities[0].code : null });
    } else if (!selected && this.entities.length === 1) {
      this.paymentForm.patchValue({ entityCode: this.entities[0].code });
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
    }
  }

  save() {
    const val = this.paymentForm.value;
    if (this.paymentForm.invalid) {
      Swal.fire('Atención', 'Verifique los campos obligatorios. El monto debe ser mayor a cero.', 'warning');
      return;
    }

    if (val.movementType !== 'PAGO' && !val.details?.trim()) {
      Swal.fire('Atención', 'Debe ingresar la glosa o motivo del ajuste contable', 'warning');
      return;
    }

    if (val.movementType === 'PAGO' && !this.selectedFile) {
      Swal.fire('Atención', 'Debe adjuntar el comprobante bancario del pago', 'warning');
      return;
    }

    const allowed = this.entities.some((e) => Number(e.code) === Number(val.entityCode));
    if (!allowed) {
      Swal.fire('Atención', 'La entidad no está asignada a su rol activo.', 'warning');
      return;
    }

    this.isSaving = true;
    const formData = new FormData();
    const period = this.periods.find(p => p.id === val.periodId);

    formData.append('movementType', val.movementType);
    formData.append('entityCode', val.entityCode);
    if (val.periodId) {
      formData.append('periodId', val.periodId);
      if (period) {
        formData.append('year', period.year);
        formData.append('period', period.period);
      }
    }
    formData.append('amount', val.amount);
    formData.append('paymentDate', val.paymentDate);
    formData.append('details', val.details ?? '');
    if (this.selectedFile) {
      formData.append('file', this.selectedFile);
    }

    this.paymentService.create(formData).subscribe({
      next: () => {
        Swal.fire('Éxito', 'Registro procesado correctamente', 'success');
        this.isSaving = false;
        this.onSave.emit();
      },
      error: (err) => {
        console.error(err);
        this.isSaving = false;
        Swal.fire('Error', err?.error?.error || 'No se pudo procesar el registro', 'error');
      }
    });
  }

  close() {
    this.onClose.emit();
  }
}
